import { Box, HStack, VStack, ArrowDownIcon, ArrowUpIcon, useTheme, Image } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Car01Icon, ClockStopWatchIcon, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { formatTo, formatToShortDateTime, stringToDateLocal } from '@app/utils/date-formatter';
import { WidgetBox, WidgetBoxProps } from '@app/features/widgets/widget-box';
import { useGetWidgetData } from '@app/features/speed-of-service';
import { useSiteTime } from '@app/features/store-core';

type WidgetSpeedOfServiceProps = WidgetBoxProps & {};

export const WidgetSpeedOfService = ({ ...props }: WidgetSpeedOfServiceProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const siteTime = useSiteTime();
  const daypart = siteTime.toDaypartNow();
  const widgetData = useGetWidgetData({
    date: daypart?.startTime,
    siteId: siteTime.siteId,
  });

  let isOffline = widgetData.data?.importerStatus?.toLowerCase() !== 'online';
  let lastRecordDateTime;

  const daypartLaneGoal = widgetData.data?.daypartTotal?.laneTotalGoal ?? 0;
  const daypartLaneTotal = widgetData.data?.daypartTotal?.laneTotal ?? 0;

  const dayTotalLaneGoal = widgetData.data?.dayTotal.laneTotalGoal ?? 0;
  const dayTotalLane = widgetData.data?.dayTotal.laneTotal ?? 0;

  if (widgetData.data?.lastRecordDateTime) {
    lastRecordDateTime = stringToDateLocal(widgetData.data?.lastRecordDateTime);
  }

  return (
    <WidgetBox hideTitle {...props}>
      <HStack alignItems="center" justifyContent="space-between" mb={1}>
        <Text size="sm" fontWeight={700} color="gray.600" lineHeight="md" textTransform="uppercase" numberOfLines={1} ellipsizeMode="tail">
          {translate(props?.title, props?.titleTranslations)}
        </Text>

        {!!widgetData.data?.portalImageUrl && (
          <Image
            source={{
              uri: widgetData.data.portalImageUrl,
            }}
            bg="gray.100"
            style={{ aspectRatio: 1, width: 50, height: 22 }}
            alt={t('widgets:speed_of_service')}
            resizeMethod="scale"
            rounded="md"
            justifyContent="center"
          />
        )}
      </HStack>

      <VStack space={1}>
        <HStack justifyContent="space-between" alignItems="center" p={2} width="full" bgColor="gray.100" minH={36} borderRadius="md">
          <Box flex={1} maxWidth="45%">
            <Text size="lg" fontWeight={700} color="black">
              {daypart ? translate(daypart.label, daypart.translations) : ''}
            </Text>
          </Box>

          <HStack flex={1} space={1.5}>
            <HStack flex={1} space={1} alignItems="center">
              <Car01Icon size="20px" color={colors.gray[700]} />
              <Text size="xl" fontWeight={700} color="black">
                {widgetData.data?.daypartTotal?.totalCars ?? 0}
              </Text>
            </HStack>

            <HStack flex={1} space={1.5} alignItems="center">
              <ClockStopWatchIcon size="20px" color={colors.gray[700]} />
              <Text size="xl" fontWeight={700} color="black">
                {daypartLaneTotal}
              </Text>
              {!!daypartLaneGoal && !!daypartLaneTotal && (
                <>
                  {daypartLaneTotal > daypartLaneGoal ? (
                    <ArrowDownIcon size="14px" color="success.600" />
                  ) : (
                    <ArrowUpIcon size="14px" color="error.600" />
                  )}
                </>
              )}
            </HStack>
          </HStack>
        </HStack>

        <HStack justifyContent="space-between" alignItems="center" p={2} width="full" bgColor="blueLight.100" minH={36} borderRadius="md">
          <Box flex={1} maxWidth="45%">
            <Text size="lg" fontWeight={700} color="black">
              {!!daypart?.startTime ? formatTo(daypart.startTime, 'ddd MM/DD') : ''}
            </Text>
          </Box>

          <HStack flex={1} space={1.5}>
            <HStack flex={1} space={1} alignItems="center">
              <Car01Icon size="20px" color={colors.gray[700]} />
              <Text size="xl" fontWeight={700} color="black">
                {widgetData.data?.dayTotal?.totalCars ?? 0}
              </Text>
            </HStack>

            <HStack flex={1} space={1.5} alignItems="center">
              <ClockStopWatchIcon size="20px" color={colors.gray[700]} />
              <Text size="xl" fontWeight={700} color="black">
                {dayTotalLane}
              </Text>
              {!!dayTotalLaneGoal && !!dayTotalLane && (
                <>
                  {dayTotalLane > dayTotalLaneGoal ? (
                    <ArrowDownIcon size="14px" color="success.600" />
                  ) : (
                    <ArrowUpIcon size="14px" color="error.600" />
                  )}
                </>
              )}
            </HStack>
          </HStack>
        </HStack>

        {!!widgetData.data?.importerStatus && (
          <HStack justifyContent="space-between" alignItems="center">
            <Box px={1.5} py={1} minH={5} borderRadius="sm" bgColor={isOffline ? 'error.50' : 'success.50'}>
              <Text size="xs" fontWeight={500} color={isOffline ? 'error.700' : 'success.700'}>
                {t('widgets:api_service')}: {t(`widgets:${widgetData.data?.importerStatus.toLowerCase()}`)}
              </Text>
            </Box>

            {!!lastRecordDateTime && (
              <Text size="xs" fontWeight={600} color="gray.600" lineHeight="xs">
                {t('widgets:last_record')}: {formatToShortDateTime(lastRecordDateTime)}
              </Text>
            )}
          </HStack>
        )}
      </VStack>
    </WidgetBox>
  );
};
